import {doc} from '../docs.js';
import {makeHull} from "effects/midi/mappable/convexHull.js"
import {mod} from "utils/mod.js"
import {getGeometryFlat} from "effects/midi/mappable/lang/geometry.js";
import {isNumber} from 'utils/IsNumber.js';
import Delaunator from 'delaunator';

doc("centroid",
    {
        "pts": ""
    },
    "Centoid of ist of poinnts",
   );

export const centroid = () => {
    return ([pts]) => {
        let sumX = 0;
        let sumY = 0;
        for (let i=0; i < pts.length; i+=2) {
            sumX += pts[i];
            sumY += pts[i];
        }
        return [[sumX/(pts.length/2), sumY/(pts.length/2)]];
    };
};

doc(
    "dHull",
    {
        "pts":"",
        "length":""
    },
    "computes hull for pts index",
    undefined,
    {
        inlets: 2,
        isHot: true,
        tags: "Graphics"
    });

export const dHull = (object, interiorPoints) => {

    // the delaunators used for calculating hull
    let delaunator1;
    let delaunator2;

    let pointsNotInHull;
    let outputArray;

    let hullRadians = [];

    return ([pts]) => {
        if (!pts) {
            return undefined;
        }

        /*
        let _pts = [];
        for (let i=0; i < pts.length; i+=2) {
            _pts.push({x: pts[i], y:pts[i+1]});
        }

        let [[_x, _y]] = centroid(0)([pts]);
        let _centroid = {x:_x,y:_y};

        let ret = makeHull(_pts, _centroid, 12);
        let _ret = [];
        for (let pt of ret) {
            _ret.push(pt.x);
            _ret.push(pt.y);
        }
        return [Float32Array.from(_ret)];
        */

        if (delaunator1 && delaunator1.coords.length === pts.length) {
            delaunator1.coords.set(pts, 0);
            delaunator1.update();
        } else {
            delaunator1 = new Delaunator(pts);
        }

        let hull1 = delaunator1.hull;
        let numPointsNotInHull = pts.length/2 - hull1.length;

        if (!pointsNotInHull || pointsNotInHull.length !== numPointsNotInHull*2) {
            pointsNotInHull = new Float32Array(numPointsNotInHull*2);
        }

        calculatePointsNotInHull(pts, hull1, pointsNotInHull);

        // get the convex hull of the interior points
        if (!delaunator2 || delaunator2.coords.length !== pointsNotInHull.length) {
            delaunator2 = new Delaunator(pointsNotInHull);
        } else {
            delaunator2.coords.set(0, pointsNotInHull);
            delaunator2.update();
        }

        // grab the calculate convex hull
        let hull2 = delaunator2.hull;

        // actually not a good idea to sort by how close to the hull...
        sortHullByDistance(pts, pointsNotInHull, hull1, hull2);

        // how many of these points do we want to include?
        let numPointsToAdd = Math.min(interiorPoints() || 0, hull2.length);

        // the output array will have numPointsToAdd + hull1.length * 2 elements
        // create this array if we need to, otherwise reuse it (if the lengths haven't changed)
        let totalNumOfPoints = numPointsToAdd + hull1.length;

        let outputArray = new Float32Array(totalNumOfPoints*2);

        let counter = 0;
        let i=0;
        for (let index of hull1) {
            outputArray[counter++] = pts[2*index];
            outputArray[counter++] = pts[2*index+1];
            i++;
        }

        // calculate the centroid of all the points
        let [[x,y]] = centroid(0)([pts]);

        let skipped = 0;
        // calculate the angle of each point in the initial hull AND the centroid
        hullRadians.length = 0;
        for (let index of hull1) {
            let x1 = pts[2*index];
            let y1 = pts[2*index+1];
            let rad = mod(Math.atan2(y1-y, x1-x), 2*Math.PI);
            hullRadians.push(rad);
        }

        // now we go through the second hull-- hull2-- and decide where to order it
        counter = 0;
        for (let index of hull2) {
            if (++counter > numPointsToAdd) {
                break;
            }

            let [x1, y1] = [pointsNotInHull[2*index], pointsNotInHull[2*index+1]];

            let rad =  mod(Math.atan2(y1-y, x1-x),2*Math.PI);
            let positionToFit = findPositionToFit(hullRadians, rad, outputArray, x1, y1);
            if (positionToFit === undefined) {
                let _rad = 2*Math.PI - rad;
                positionToFit = findPositionToFit(hullRadians, rad, outputArray, x1, y1);
                if (positionToFit === undefined) {
                    skipped++;
                    continue;
                }
            }

            hullRadians.splice(positionToFit, 0, rad);

            //console.log("hullRadians became =", [... hullRadians]);
            splice(outputArray, 2*positionToFit, 0, x1, y1);
        }

        if (skipped > 0) {
            //console.log("skipped = ", skipped);
            let _out = new Float32Array(outputArray.length - skipped*2);
            for (let i=0; i < (outputArray.length / 2) - skipped; i++) {
                _out[i*2] = outputArray[i*2];
                _out[i*2+1] = outputArray[i*2+1];
            }
            return _out;
        }
        return [outputArray];
   };
}


// units = radians
const isPointBetweenAB = (a,b,pt) => {
    return (a < pt && pt < b) ||
        (a > pt && pt > b);
};

// given a pt with angle from centroid = rad
// and a list of all the angles (in radians) of pts wrt to the centroid
// 
const findPositionToFit = (hullRadians, rad, output, x, y) => {
    if (hullRadians.length === 1) {
        return 0;
    }
    //console.log("rad=%s hullRadians=", rad, [... hullRadians]);

    let len = hullRadians.length;
    let choices = [];
    const DIST_MIN = 20;
    const DIST_MAX = 140;
    for (let i=0; i < len; i++) {
        let a = i;
        let b = (i+1) % len;

        if (dist(output[a*2], output[a*2+1], output[b*2], output[b*2+1]) < DIST_MIN) {
            // dont want to put point between really close points
            continue;
        }
        if (isPointBetweenAB(hullRadians[a], hullRadians[b], rad) ||
            (i === len - 1 && isPointBetweenAB(hullRadians[a], 2*Math.PI - hullRadians[b], rad))) {
            //console.log("rad =%s was between %s and %s", rad, hullRadians[a], hullRadians[b], a,b);
            choices.push(a);
        }
    }

    // now go thru the choices and find the one that has the smallest distance
    //return choices[0] || 0;

    let minDistance = 100000;
    let _choice = undefined;
    //console.log("X=%s y=%s", x,y);
    for (let choice of choices) {
        let a = choice;
        let b = (choice + 1) % len;
        let _dist = (dist(output[a*2], output[a*2+1], x, y) + dist(output[b*2], output[b*2+1], x,y))/2;
        //console.log("dist between a=%s b=%s =", a,b,_dist, dist(output[a*2], output[a*2+1], x, y),
        //           dist(output[b*2], output[b*2+1], x,y));
        //console.log('a=(%s,%s) b=(%s,%s)', output[a*2], output[a*2+1], output[b*2], output[b*2+1]);
        if (b === 0) {
            _dist *= 1.7;
        }
        if (_dist > DIST_MAX) {
            continue;
        }
        if (_dist < minDistance) {
            minDistance = _dist;
            //console.log("chose", b);
            _choice = b;
        }
    }
    //console.log("choice was ", _choice);
    return _choice;
};


const calculatePointsNotInHull = (pts, hull, out) => {
    // now get all points not in hull
    let counter=0;
    for (let i=0; i < pts.length; i+=2) {
        let index = i/2;
        if (!hull.includes(index)) {
            out[counter++] = pts[i];
            out[counter++] = pts[i+1];
        }
    }
};
 

function splice(arr, starting, deleteCount, ... elements) {
    if (arguments.length === 1) {
        return arr;
    }
    starting = Math.max(starting, 0);
    deleteCount = Math.max(deleteCount, 0);
    elements = elements || [];
    
    arr.set(arr.subarray(starting + deleteCount, arr.length - elements.length), starting + elements.length);
    arr.set(elements, starting);
    return arr;
};

// sorts the second hull in order of how close each pt is
// to any pt in the first hull
const sortHullByDistance = (pts1, pts2, hull1, hull2)=> {
    hull2.sort(
        (a,b) => getMinDistanceFromHull(pts1, pts2, a, hull1) -
        getMinDistanceFromHull(pts1, pts2, b, hull1));
};

const getMinDistanceFromHull = (originalPts, newPts, index, originalHull) => {
    let min = 100000;
    let x1 = newPts[2*index];
    let y1 = newPts[2*index+1];

    for (let index of originalHull) {
        let x = originalPts[2*index];
        let y = originalPts[2*index+1];
        let d = dist(x,y,x1,y1);
        if (d < 10) {
            d = d*10000;
        }
        if (d < min) {
            min = d;
        }
    }
    return min;
};

const pruneClose = (arr) => {
    let out = [];
    for (let i=0; i < arr.length; i+=2 ){
        let x = arr[i];
        let y = arr[i+1];

        let skip = false;
        for (let j=0; j < out.length; j+=2) {
            if (dist(out[j], out[j+1], x, y) < 15) {
                skip=true;
                continue;
            }
        }
        if (!skip) {
            out.push(x);
            out.push(y);
        }
    }
    return out;
};

const dist = (x1,y1,x2,y2) => Math.sqrt(Math.pow(x1-x2, 2) + Math.pow(y1-y2, 2));
